import { ChangeEvent, FC } from 'react'
import { useFormContext } from 'react-hook-form'

type InputProps = {
  children?: unknown
  id?: string
  value?: string
  defaultValue?: string
  placeholder?: string
  name: string
  type?: string
  min?: number
  max?: number
  disabled?: boolean
  className?: string
  autoFocus?: boolean
  autoComplete?: string
  onBlur?: () => void
  onChange?: ((e: ChangeEvent<HTMLInputElement>) => void) | (() => void)
  onKeyDown?: () => void
  setIsChanged?: (changed: boolean) => void
  isChecked?: boolean
}

export const Input: FC<InputProps> = ({
  name,
  value,
  defaultValue,
  placeholder,
  id,
  disabled,
  autoFocus,
  autoComplete,
  onBlur,
  className,
  onChange,
  onKeyDown,
  type,
  min,
  max,
  setIsChanged,
  isChecked,
}) => {
  const { register, getValues } = useFormContext()
  const uniqueId = `inputText${id ? id : name}`

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    const currentValue = getValues(name)
    
    // For checkboxes, compare checked state; for other inputs, compare value
    const shouldSetChanged = type === 'checkbox' 
      ? e.target.checked !== currentValue 
      : e.target.value !== currentValue

    if (setIsChanged && shouldSetChanged) {
      setIsChanged(true)
    }

    if (onChange) {
      if (onChange.length === 0) {
        const func = onChange as () => void
        func()
      } else {
        const func = onChange as (e: ChangeEvent<HTMLInputElement>) => void
        func(e)
      }
    }
  }

  const handleOnBlur = (e: ChangeEvent<HTMLInputElement>) => {
    // Save email and phone to localStorage for no-digital appointments
    if (name === 'email' && e.target.value) {
      localStorage.setItem('noDigitalEmail', e.target.value)
    } else if (name === 'phone' && e.target.value) {
      localStorage.setItem('noDigitalPhone', e.target.value)
    }

    // Call the original onBlur if provided
    if (onBlur) {
      onBlur()
    }
  }

  const registerOptions = {
    onBlur: handleOnBlur,
    min,
    max,
    disabled,
    ...(onChange ? { onChange: handleOnChange } : {})
  }


  return (
    <input
      {...register(name, registerOptions)}
      id={uniqueId}
      data-testid={`inputText${name}`}
      alt={uniqueId}
      {...(type === 'checkbox' ? 
        (isChecked !== undefined ? { checked: isChecked } : {}) : 
        { value, defaultValue }
      )}
      placeholder={placeholder}
      autoFocus={autoFocus}
      autoComplete={autoComplete}
      className={className}
      type={type}
      min={min}
      max={max}
      onKeyDown={onKeyDown}
    />
  )
}
